import { useMemo } from 'react'
import { keepPreviousData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import { ORDERS_PAGE_SIZE, type OrderRow, type OrdersQuery } from '@/api/revenueLeakage'

import type { Paging } from '../../list/ListStates'

/**
 * The Orders tab's pages, read by the API's keyset cursor at `ORDERS_PAGE_SIZE`: each page
 * starts after the last row of the one before, so a row that moved while the list loaded
 * is neither repeated nor skipped — the footer's "list changed" note never applies.
 * Keyed by the query less its cursor: `useInfiniteQuery` keeps the pages within one entry.
 */
export function useRevenueOrders(query: OrdersQuery) {
  const client = useApiClient()
  const queryClient = useQueryClient()
  const queryKey = ['revenue-stage-b', 'orders', client.kind, query] as const
  const result = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => client.revenueOrders({ ...query, cursor: pageParam, limit: ORDERS_PAGE_SIZE }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.data.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  })
  const pages = result.data?.pages
  const rows = useMemo<OrderRow[]>(() => pages?.flatMap((page) => page.data.rows) ?? [], [pages])
  const first = pages?.[0]
  const latest = pages?.[pages.length - 1]
  const paging: Paging = {
    hasMore: result.hasNextPage,
    loadingMore: result.isFetchingNextPage,
    moreFailed: result.isFetchNextPageError,
    incomplete: false,
    onMore: () => void result.fetchNextPage(),
    onRestart: () => void queryClient.resetQueries({ queryKey, exact: true }),
  }
  return { ...result, rows, first, latest, paging }
}
